import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {Router} from "@angular/router";
import {StartupService} from "../../service-ui/startup.service";
import {NavbarService} from "../../service-ui/navbar.service";
import {SubscriptionService} from "../../service-data/subscription.service";
import {LoggedInCallback} from "../../service-auth/cognito.service";
import {EcommerceConnection} from "../../../model/EcommerceConnection";
import {EcommerceConnectionService} from "../../service-data/ecommerce-connection.service";
import {CarrierServiceMap} from "../../../model/CarrierServiceMap";
import {CarrierServiceMapService} from "../../service-data/carrier-service-map.service";
import {LanguagePipe} from "../../pipe/language.pipe";
import {CARRIER_SERVICE_LEVELS} from "../../util/carrier-service-levels";

@Component({
  selector: 'app-ecommerce-connection',
  templateUrl: './ecommerce-connection.component.html',
  styleUrls: ['./ecommerce-connection.component.css']
})
export class EcommerceConnectionComponent implements OnInit, OnChanges, LoggedInCallback {
  @Input() ecommerceConnection: EcommerceConnection;
  editEcommerceConnection: EcommerceConnection;
  editing = false;
  carrierServiceMappings: CarrierServiceMap[] = [];
  dirtyMappings: Set<CarrierServiceMap> = new Set();
  mappingSnapshots: Map<CarrierServiceMap, string> = new Map();
  carrierServiceMapLoading = false;
  carrierServiceMapLoaded = false;
  showDefaultMappings = false;
  defaultMappings: CarrierServiceMap[] = [];
  showSecrets: { [key: string]: boolean } = {};
  private originalSnapshot: string;

  carrierServiceLevels = CARRIER_SERVICE_LEVELS;

  constructor(public router: Router, public startupService: StartupService, private ecommerceConnectionService: EcommerceConnectionService, private navbarService: NavbarService, public subscriptionService: SubscriptionService, private carrierServiceMapService: CarrierServiceMapService) {
    this.editEcommerceConnection = new EcommerceConnection(this.ecommerceConnection);
  }

  get hasChanges(): boolean {
    if (!this.ecommerceConnection) {
      return false;
    }
    return JSON.stringify(this.ecommerceConnection) !== this.originalSnapshot;
  }

  get isUsingDefaultMap(): boolean {
    return this.ecommerceConnection.carrierServiceMap_ID === 0
      || this.ecommerceConnection.carrierServiceMap_ID === this.getDefaultMapId();
  }

  /**
   * The connection's product-matching mode, normalized for display.
   *
   * Existing rows can hold `NULL` or `''`, and the server treats those -- plus
   * any value it does not recognise -- as `ECOMMERCE_PRODUCT`. Without this the
   * radio group would render with nothing selected for those connections, which
   * reads as "unconfigured" when the behaviour is in fact well defined.
   *
   * See `docs/contracts/shared/EcommerceConnection.md`.
   */
  get productIdentifierType(): string {
    const value = this.ecommerceConnection && this.ecommerceConnection.productIdentifierType
      ? String(this.ecommerceConnection.productIdentifierType).trim()
      : '';
    if ( value === 'CUSTOMER_PRODUCT_ID' || value === 'EAN13' || value === 'INTERNAL_PRODUCT_ID' ) {
      return value;
    }
    return 'ECOMMERCE_PRODUCT';
  }

  /**
   * Writes the chosen mode back to the model.
   *
   * Selecting the option that a NULL row was already behaving as does count as
   * a change and will enable Save. That is intentional -- it replaces an
   * implicit default with an explicit value.
   */
  setProductIdentifierType(value: string): void {
    if ( !this.ecommerceConnection ) {
      return;
    }
    this.ecommerceConnection.productIdentifierType = value;
  }

  get hasCustomMap(): boolean {
    return this.ecommerceConnection.carrierServiceMap_ID !== 0
      && this.ecommerceConnection.carrierServiceMap_ID !== this.getDefaultMapId();
  }

  ngOnInit() {
    this.startupService.isAuthenticated(this);
    this.editEcommerceConnection = new EcommerceConnection(this.ecommerceConnection);
    this.originalSnapshot = JSON.stringify(this.ecommerceConnection);
  }

  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (!isLoggedIn) {
      console.log("Not logged in in Account");
      // this.router.navigate(['/home/login']);
    } else {
    }
  }
  edit() {
    this.editing = true;
    this.editEcommerceConnection = new EcommerceConnection(this.ecommerceConnection);
    this.originalSnapshot = JSON.stringify(this.ecommerceConnection);
  }

  cancel() {
    this.editing = false;
    this.editEcommerceConnection = new EcommerceConnection(this.ecommerceConnection);
    this.originalSnapshot = JSON.stringify(this.ecommerceConnection);
  }

  save(element) {
    element.classList.add('spinner');
    this.ecommerceConnectionService.updateItem(this.ecommerceConnection).subscribe(newEcommerceConnection => {
      console.log(`EcommerceConnection updated in save`);
      element.classList.remove('spinner');
      this.originalSnapshot = JSON.stringify(this.ecommerceConnection);
      this.editing = false;
    });
  }

  getOrderReleaseMode(): string {
    if (!this.ecommerceConnection.automaticallyReleaseOrders) {
      return 'MANUAL';
    }
    if (this.ecommerceConnection.automaticallyReleaseOrderHoldNumberOfHours === 0) {
      return 'IMMEDIATE';
    }
    return 'DELAYED';
  }

  setOrderReleaseMode(mode: string): void {
    switch (mode) {
      case 'MANUAL':
        this.ecommerceConnection.automaticallyReleaseOrders = false;
        this.ecommerceConnection.automaticallyReleaseOrderHoldNumberOfHours = 0;
        break;
      case 'IMMEDIATE':
        this.ecommerceConnection.automaticallyReleaseOrders = true;
        this.ecommerceConnection.automaticallyReleaseOrderHoldNumberOfHours = 0;
        break;
      case 'DELAYED':
        this.ecommerceConnection.automaticallyReleaseOrders = true;
        if (!this.ecommerceConnection.automaticallyReleaseOrderHoldNumberOfHours) {
          this.ecommerceConnection.automaticallyReleaseOrderHoldNumberOfHours = 24;
        }
        break;
    }
  }

  onShippingPanelOpened(): void {
    if (!this.carrierServiceMapLoaded) {
      this.carrierServiceMapLoading = true;
      this.carrierServiceMapService.getItems(true).subscribe(() => {
        this.carrierServiceMapLoading = false;
        this.carrierServiceMapLoaded = true;
        this.loadCarrierServiceMappings();
        this.loadDefaultMappings();
      });
    }
  }

  loadCarrierServiceMappings(): void {
    this.carrierServiceMappings = [];
    this.dirtyMappings.clear();
    this.mappingSnapshots.clear();
    const mapId = this.ecommerceConnection.carrierServiceMap_ID;
    if (mapId && mapId !== 0 && mapId !== this.getDefaultMapId()) {
      this.carrierServiceMappings = this.carrierServiceMapService.objects
        .filter(csm => csm.map_Id === mapId);
      for (const m of this.carrierServiceMappings) {
        this.snapshotMapping(m);
      }
    }
  }

  loadDefaultMappings(): void {
    const defaultMapId = this.getDefaultMapId();
    this.defaultMappings = this.carrierServiceMapService.objects
      .filter(csm => csm.map_Id === defaultMapId);
  }

  toggleDefaultMappings(): void {
    this.showDefaultMappings = !this.showDefaultMappings;
  }

  getCarrierServiceLabel(id: string): string {
    const cs = this.carrierServiceLevels.find(c => c.ID === id);
    return cs ? `${cs.carrier} — ${cs.serviceLevel}` : id;
  }

  getDefaultMapLabel(): string {
    return LanguagePipe.get(this.ecommerceConnection.type);
  }

  addDefaultMappingToCustom(defaultMapping: CarrierServiceMap): void {
    if (this.isDefaultCodeAlreadyInCustom(defaultMapping)) {
      return;
    }
    const newMapping = new CarrierServiceMap({
      org_ID: this.ecommerceConnection.org_ID,
      map_Id: this.ecommerceConnection.carrierServiceMap_ID,
      customerCarrierService: defaultMapping.customerCarrierService,
      carrierService: defaultMapping.carrierService
    });
    this.carrierServiceMapService.addItem(newMapping).subscribe(added => {
      this.carrierServiceMappings.push(added);
      this.snapshotMapping(added);
    });
  }

  isDefaultCodeAlreadyInCustom(defaultMapping: CarrierServiceMap): boolean {
    return this.carrierServiceMappings.some(m =>
      m.customerCarrierService === defaultMapping.customerCarrierService && defaultMapping.customerCarrierService !== ''
    );
  }

  isCustomCodeInDefaults(mapping: CarrierServiceMap): boolean {
    return this.defaultMappings.some(d =>
      d.customerCarrierService === mapping.customerCarrierService && mapping.customerCarrierService !== ''
    );
  }

  createCustomCarrierMap(populateFromDefaults: boolean = false): void {
    this.ecommerceConnection.carrierServiceMap_ID = this.ecommerceConnection.ID;
    this.ecommerceConnectionService.updateItem(this.ecommerceConnection).subscribe(() => {
      this.originalSnapshot = JSON.stringify(this.ecommerceConnection);
      if (populateFromDefaults) {
        this.carrierServiceMapLoading = true;
        const defaultMapId = this.getDefaultMapId();
        const defaults = this.carrierServiceMapService.objects
          .filter(csm => csm.map_Id === defaultMapId);
        let remaining = defaults.length;
        if (remaining === 0) {
          this.carrierServiceMapLoading = false;
          return;
        }
        for (const defaultMapping of defaults) {
          const newMapping = new CarrierServiceMap({
            org_ID: this.ecommerceConnection.org_ID,
            map_Id: this.ecommerceConnection.ID,
            customerCarrierService: defaultMapping.customerCarrierService,
            carrierService: defaultMapping.carrierService
          });
          this.carrierServiceMapService.addItem(newMapping).subscribe(added => {
            this.carrierServiceMappings.push(added);
            this.snapshotMapping(added);
            remaining--;
            if (remaining === 0) {
              this.carrierServiceMapLoading = false;
            }
          });
        }
      }
    });
  }

  getDefaultMapId(): number {
    switch (this.ecommerceConnection.type) {
      case 'SHOPIFY': return 1;
      case 'WOOCOMMERCE': return 2;
      default: return 1;
    }
  }

  populateFromDefaults(): void {
    const defaultMapId = this.getDefaultMapId();
    const defaults = this.carrierServiceMapService.objects
      .filter(csm => csm.map_Id === defaultMapId);
    for (const defaultMapping of defaults) {
      const newMapping = new CarrierServiceMap({
        org_ID: this.ecommerceConnection.org_ID,
        map_Id: this.ecommerceConnection.carrierServiceMap_ID,
        customerCarrierService: defaultMapping.customerCarrierService,
        carrierService: defaultMapping.carrierService
      });
      this.carrierServiceMapService.addItem(newMapping).subscribe(added => {
        this.carrierServiceMappings.push(added);
        this.snapshotMapping(added);
      });
    }
  }

  addMapping(): void {
    const newMapping = new CarrierServiceMap({
      org_ID: this.ecommerceConnection.org_ID,
      map_Id: this.ecommerceConnection.carrierServiceMap_ID,
      customerCarrierService: '',
      carrierService: this.carrierServiceLevels[0].ID
    });
    this.carrierServiceMapService.addItem(newMapping).subscribe(added => {
      this.carrierServiceMappings.push(added);
      this.snapshotMapping(added);
      this.dirtyMappings.add(added);
    });
  }

  deleteMapping(mapping: CarrierServiceMap): void {
    this.carrierServiceMapService.deleteItem(mapping).subscribe(() => {
      const idx = this.carrierServiceMappings.indexOf(mapping);
      if (idx !== -1) {
        this.carrierServiceMappings.splice(idx, 1);
      }
      this.dirtyMappings.delete(mapping);
      this.mappingSnapshots.delete(mapping);
    });
  }

  saveMapping(mapping: CarrierServiceMap): void {
    this.carrierServiceMapService.updateItem(mapping).subscribe(() => {
      this.dirtyMappings.delete(mapping);
      this.snapshotMapping(mapping);
    });
  }

  snapshotMapping(mapping: CarrierServiceMap): void {
    this.mappingSnapshots.set(mapping, JSON.stringify({
      customerCarrierService: mapping.customerCarrierService,
      carrierService: mapping.carrierService
    }));
  }

  markMappingDirty(mapping: CarrierServiceMap): void {
    const snapshot = this.mappingSnapshots.get(mapping);
    const current = JSON.stringify({
      customerCarrierService: mapping.customerCarrierService,
      carrierService: mapping.carrierService
    });
    if (snapshot && snapshot === current) {
      this.dirtyMappings.delete(mapping);
    } else {
      this.dirtyMappings.add(mapping);
    }
  }

  isDuplicateCustomerCode(mapping: CarrierServiceMap): boolean {
    return this.carrierServiceMappings.some(m =>
      m !== mapping && m.customerCarrierService === mapping.customerCarrierService && mapping.customerCarrierService !== ''
    );
  }

  /**
   * Re-initialize when the parent swaps in a different connection.
   *
   * `EcommerceComponent` renders exactly one of these and changes the
   * `[ecommerceConnection]` binding when the operator picks another
   * store, so Angular reuses this instance and `ngOnInit()` never runs
   * again. Everything derived from the input therefore belonged to the
   * previously selected store: `originalSnapshot` (so `hasChanges` lit
   * up Save against the wrong row the moment the panel re-rendered),
   * `editEcommerceConnection`, the loaded carrier-service mappings,
   * and the revealed-secret toggles. The edit session is closed too --
   * carrying "I am editing" across a store switch is how edits end up
   * saved onto a store the operator never opened.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['ecommerceConnection'];
    if ( !change || change.isFirstChange() ) {
      return;
    }
    if ( change.previousValue === change.currentValue ) {
      return;
    }
    this.editing = false;
    this.editEcommerceConnection = new EcommerceConnection(this.ecommerceConnection);
    this.originalSnapshot = JSON.stringify(this.ecommerceConnection);
    this.showSecrets = {};
    this.showDefaultMappings = false;
    this.carrierServiceMappings = [];
    this.defaultMappings = [];
    this.dirtyMappings.clear();
    this.mappingSnapshots.clear();
    // The CarrierServiceMap collection itself is app-wide and still
    // loaded; only this connection's slice of it has to be recomputed.
    if ( this.carrierServiceMapLoaded ) {
      this.loadCarrierServiceMappings();
      this.loadDefaultMappings();
    }
  }
}
